import { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RotateCcw, Check } from 'lucide-react';
import { resetOnboardingFlags } from '@/utils/devResetOnboarding';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { resetTourFlags } from '@/hooks/useTourController';

// ALL onboarding-related localStorage keys (both canonical + legacy variants)
const ALL_ONBOARDING_KEYS = [
  // Canonical keys (underscore)
  'therappi_pre_auth_welcome_seen',
  'therappi_pre_auth_typewriter_seen',
  'therappi_tour_completed',
  'therappi_conversational_onboarding_completed',
  'therappi_conversational_onboarding_just_completed',
  'therappi_welcome_seen',
  'therappi_tour_requested',
  // Legacy keys (hyphen)
  'therappi-pre-auth-welcome-seen',
  'therappi-pre-auth-typewriter-seen',
  'therappi-first-time-tour-seen',
  'therappi-tour-requested',
  // Other variants
  'therappi_welcome_seen_/',
];

interface DeveloperResetOnboardingProps {
  userId?: string;
}

export function DeveloperResetOnboarding({ userId }: DeveloperResetOnboardingProps) {
  const [isResetting, setIsResetting] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);
  const { toast } = useToast();

  const handleReset = async () => {
    setIsResetting(true);
    setResetComplete(false);

    try {
      // 1. Reset Supabase onboarding_completed FIRST (before clearing local state)
      if (userId) {
        const { data, error } = await supabase
          .from('profiles')
          .update({ onboarding_completed: false })
          .eq('user_id', userId)
          .select('onboarding_completed')
          .maybeSingle();

        if (error) {
          console.error('[DEV] Failed to reset Supabase onboarding_completed:', error);
          toast({
            title: 'Database Reset Failed',
            description: 'Could not reset onboarding_completed in database. Check console.',
            variant: 'destructive',
          });
          setIsResetting(false);
          return;
        } else {
          console.log('[DEV] Supabase onboarding_completed reset to:', data?.onboarding_completed, 'for user_id:', userId);
        }
      }

      // 2. Clear ALL localStorage flags (both canonical + legacy variants)
      console.log('[DEV] Clearing all onboarding localStorage keys...');
      ALL_ONBOARDING_KEYS.forEach(key => {
        console.log(`[DEV] Removing: ${key}`);
        localStorage.removeItem(key);
      });
      
      // Also clear any welcome_seen keys with path suffixes
      Object.keys(localStorage).forEach(key => {
        if (key.includes('welcome_seen') || key.includes('tour') || key.includes('onboarding')) {
          console.log(`[DEV] Removing (pattern match): ${key}`);
          localStorage.removeItem(key);
        }
      });
      
      // 3. Reset module-level state
      resetOnboardingFlags();
      resetTourFlags();
      console.log('[DEV] Module-level state reset');

      // 4. Sign out to force clean auth state
      await supabase.auth.signOut();
      console.log('[DEV] Signed out');

      setResetComplete(true);
      toast({
        title: 'Onboarding Reset Complete',
        description: 'All flags cleared. Redirecting to first-run experience...',
      });

      // 5. Redirect to ROOT (/) - NOT /auth
      // This ensures the state machine starts fresh and shows Pre-auth Welcome
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
    } catch (err) {
      console.error('[DEV] Reset error:', err);
      toast({
        title: 'Reset Error',
        description: 'Some flags may not have been cleared. Check console.',
        variant: 'destructive',
      });
    } finally {
      setIsResetting(false);
    }
  };

  // VISIBILITY FIX: Show in development, OR if therappi_dev_mode is set, OR in simulator builds
  const isNative = Capacitor.isNativePlatform();
  const isDevMode = (() => {
    try {
      return localStorage.getItem('therappi_dev_mode') === 'true';
    } catch {
      return false;
    }
  })();
  
  // Show if: DEV environment OR dev_mode flag OR native platform (simulator)
  const shouldShow = import.meta.env.DEV || isDevMode || isNative;
  
  if (!shouldShow) {
    return null;
  }

  return (
    <Card className="border-amber-500/50 bg-amber-500/5">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
          <AlertTriangle className="h-5 w-5" />
          Developer: Reset Onboarding
        </CardTitle>
        <CardDescription>
          Clears all onboarding flags and signs you out for a clean end-to-end test:
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
          <li>therappi_conversational_onboarding_completed</li>
          <li>therappi_conversational_onboarding_just_completed</li>
          <li>tourSeen / therappi-first-time-tour-seen</li>
          <li>tourRequested / therappi_tour_requested</li>
          <li>welcomeSeen flags</li>
          <li>Supabase onboarding_completed</li>
        </ul>
        <p className="text-xs text-muted-foreground mt-2 italic">
          Note: User data (daily check-ins, etc.) is preserved.
        </p>
        
        <Button
          onClick={handleReset}
          disabled={isResetting}
          variant="outline"
          className="w-full border-amber-500/50 hover:bg-amber-500/10"
        >
          {isResetting ? (
            <>
              <RotateCcw className="mr-2 h-4 w-4 animate-spin" />
              Resetting...
            </>
          ) : resetComplete ? (
            <>
              <Check className="mr-2 h-4 w-4" />
              Reset Complete - Redirecting...
            </>
          ) : (
            <>
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset All Onboarding Flags
            </>
          )}
        </Button>

        <p className="text-xs text-muted-foreground">
          Tip: You can also run <code className="bg-muted px-1 rounded">resetOnboardingFlags()</code> in the browser console.
        </p>
      </CardContent>
    </Card>
  );
}
